import { createLogger } from '@anddone/ajs-logger';
import type { FragmentRegistry } from '../fragment-registry/FragmentRegistry';
import type { OrchestratorCommandManager } from './OrchestratorCommandManager';
import type { OrchestratorMessageBus } from './OrchestratorMessageBus';

const logger = createLogger('SDK', 'OrchestratorLifecycleManager');

/**
 * Manages orchestrator lifecycle state and cleanup.
 */
export class OrchestratorLifecycleManager {
  #sdkInitialized = false;

  public constructor(
    private readonly messageBus: OrchestratorMessageBus,
    private readonly commandManager: OrchestratorCommandManager,
    private readonly fragmentRegistry: FragmentRegistry,
  ) {}

  /**
   * Set the SDK initialized state.
   *
   * @param initialized - Boolean indicating if app is initialized
   * @returns void
   */
  public setSdkInitialized(initialized: boolean): void {
    this.#sdkInitialized = initialized;

    logger.info('lifecycle', `App initialized state set to ${initialized}`, {
      initialized,
    });
  }

  /**
   * Get the SDK initialized state.
   *
   * @returns boolean
   */
  public getSdkInitialized(): boolean {
    return this.#sdkInitialized;
  }

  /**
   * Clean up and remove event listeners.
   *
   * @returns void
   */
  public destroy(): void {
    logger.info('lifecycle', 'Destroying orchestrator', {
      listenerCount: this.messageBus.listenerCount(),
      registeredFragmentCount: this.fragmentRegistry.size(),
      pendingCommandFragmentCount: this.commandManager.pendingFragmentCount(),
      hasMessageHandler: this.messageBus.hasMessageHandler(),
    });

    this.messageBus.removeMessageListener();
    this.messageBus.clear();
    this.commandManager.clearAllPending();

    logger.debug('lifecycle', 'Orchestrator cleanup complete');
  }
}
